<script setup>
import { computed, nextTick, provide, ref, useTemplateRef, watch } from 'vue';
import { useElementSize } from '@vueuse/core';

const props = defineProps({
  index: {
    type: Number,
    default: 0,
  },
  border: {
    type: Boolean,
    default: true,
  },
  ariaLabel: {
    type: String,
    default: 'Abas',
  },
  idBase: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['change']);

const tabsContainer = useTemplateRef('tabsContainer');
const tabsList = useTemplateRef('tabsList');
const tabRefs = ref([]);

const { width: containerWidth } = useElementSize(tabsContainer);
const { width: listWidth } = useElementSize(tabsList);

const hasScroll = ref(false);
const generatedIdBase = `woot-tabs-${Math.random().toString(36).slice(2, 8)}`;
const resolvedIdBase = computed(() => props.idBase || generatedIdBase);

const activeIndex = computed({
  get: () => props.index,
  set: newValue => {
    emit('change', newValue);
  },
});

provide('activeIndex', activeIndex);
provide('updateActiveIndex', index => {
  activeIndex.value = index;
});

const tabId = index => `${resolvedIdBase.value}-tab-${index}`;
const panelId = index => `${resolvedIdBase.value}-panel-${index}`;

const setTabRef = (index, el) => {
  tabRefs.value[index] = el;
};

const enabledIndexes = () =>
  tabRefs.value
    .map((el, index) => ({ el, index }))
    .filter(({ el }) => el && el.getAttribute('aria-disabled') !== 'true')
    .map(({ index }) => index);

const focusTab = index => {
  nextTick(() => {
    const el = tabRefs.value[index];
    if (el) el.focus();
  });
};

const selectTab = index => {
  if (tabRefs.value[index]?.getAttribute('aria-disabled') === 'true') return;
  activeIndex.value = index;
  focusTab(index);
};

const moveFocus = (fromIndex, delta) => {
  const indexes = enabledIndexes();
  if (!indexes.length) return;

  const currentPosition = Math.max(0, indexes.indexOf(fromIndex));
  const nextPosition =
    (currentPosition + delta + indexes.length) % indexes.length;
  selectTab(indexes[nextPosition]);
};

const onTabKeydown = (event, index) => {
  switch (event.key) {
    case 'ArrowRight':
    case 'ArrowDown':
      event.preventDefault();
      moveFocus(index, 1);
      break;
    case 'ArrowLeft':
    case 'ArrowUp':
      event.preventDefault();
      moveFocus(index, -1);
      break;
    case 'Home': {
      event.preventDefault();
      const firstIndex = enabledIndexes()[0];
      if (firstIndex !== undefined) selectTab(firstIndex);
      break;
    }
    case 'End': {
      event.preventDefault();
      const indexes = enabledIndexes();
      const lastIndex = indexes[indexes.length - 1];
      if (lastIndex !== undefined) selectTab(lastIndex);
      break;
    }
    case 'Enter':
    case ' ':
      event.preventDefault();
      selectTab(index);
      break;
    default:
      break;
  }
};

provide('tabA11y', {
  setTabRef,
  tabId,
  panelId,
  onTabKeydown,
});

const computeScrollWidth = () => {
  if (tabsContainer.value && tabsList.value) {
    hasScroll.value = tabsList.value.scrollWidth > tabsList.value.clientWidth;
  }
};

const onScrollClick = direction => {
  if (tabsContainer.value && tabsList.value) {
    let scrollPosition = tabsList.value.scrollLeft;
    scrollPosition += direction === 'left' ? -100 : 100;
    tabsList.value.scrollTo({
      top: 0,
      left: scrollPosition,
      behavior: 'smooth',
    });
  }
};

// Watch for changes in element sizes with immediate execution
watch(
  [containerWidth, listWidth],
  () => {
    computeScrollWidth();
  },
  { immediate: true }
);
</script>

<template>
  <div
    ref="tabsContainer"
    class="flex"
    :class="[border && 'border-b border-b-n-weak']"
  >
    <button
      v-if="hasScroll"
      class="items-center rounded-none cursor-pointer flex h-auto justify-center min-w-8"
      @click="onScrollClick('left')"
    >
      <fluent-icon icon="chevron-left" :size="16" />
    </button>
    <ul
      ref="tabsList"
      role="tablist"
      :aria-label="ariaLabel || undefined"
      class="border-r-0 border-l-0 border-t-0 flex min-w-[6.25rem] py-0 px-4 list-none mb-0"
      :class="
        hasScroll ? 'overflow-hidden py-0 px-1 max-w-[calc(100%-64px)]' : ''
      "
    >
      <slot />
    </ul>
    <button
      v-if="hasScroll"
      class="items-center rounded-none cursor-pointer flex h-auto justify-center min-w-8"
      @click="onScrollClick('right')"
    >
      <fluent-icon icon="chevron-right" :size="16" />
    </button>
  </div>
</template>
